import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, X, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = ["Custom Tumbler / Drinkware", "Metal Business Cards", "Jewelry / Pendants", "Slide Engraving & Porting", "Barrel Porting", "Bulk / Corporate Order", "Something Else"];

export default function Quote() {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", quantity: "", budget: "", message: "" });
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Please upload an image under 3MB");
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.message) {
      return toast.error("Please fill in name, email, project type and message");
    }
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, reference_image: image });
      setDone(true);
      toast.success("Quote request sent — we'll be in touch soon");
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="pt-40 pb-40 text-center max-w-xl mx-auto px-6">
        <div className="w-16 h-16 mx-auto border border-primary flex items-center justify-center mb-8"><Check className="w-8 h-8 text-primary" /></div>
        <h1 className="font-display text-5xl mb-4">Request received</h1>
        <p className="font-body text-muted-foreground">Thank you, {form.name.split(" ")[0]}. Our team reviews every custom request personally and will reach out within one business day.</p>
      </div>
    );
  }

  const inputCls = "bg-[#111] border-white/15 rounded-none h-12 mt-2 focus-visible:ring-primary";

  return (
    <div data-testid="quote-page" className="pt-36 pb-32">
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="font-num uppercase tracking-[0.3em] text-primary text-sm mb-4">Custom Work</p>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] mb-6">Tell us your idea</h1>
          <p className="font-body text-muted-foreground max-w-xl mb-12">
            One-off gift or a thousand-piece run — send us the concept and we'll come back with options, pricing, and a timeline.
          </p>
        </Reveal>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="grid sm:grid-cols-2 gap-6 border border-white/10 bg-[#0d0d0d] p-8 md:p-12"
        >
          <div>
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Name *</Label>
            <Input data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Email *</Label>
            <Input data-testid="quote-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Phone</Label>
            <Input data-testid="quote-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Project Type *</Label>
            <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
              <SelectTrigger data-testid="quote-type" className="bg-[#111] border-white/15 rounded-none h-12 mt-2"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Estimated Quantity</Label>
            <Input data-testid="quote-quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="e.g. 50" className={inputCls} />
          </div>
          <div>
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Budget Range</Label>
            <Input data-testid="quote-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="e.g. $500–$1000" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Describe your project *</Label>
            <Textarea data-testid="quote-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} className="bg-[#111] border-white/15 rounded-none mt-2 focus-visible:ring-primary" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Reference Image</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="quote-image-input" />
            <div className="mt-2 flex items-center gap-3">
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-forge flex items-center gap-2 border border-white/15 px-5 py-3 font-num uppercase text-xs tracking-widest hover:border-primary hover:text-primary">
                <Upload className="w-4 h-4" /> {image ? "Replace" : "Upload"}
              </button>
              {image && (
                <div className="flex items-center gap-2">
                  <img src={image} alt="ref" className="h-10 w-10 object-cover border border-white/15" />
                  <button type="button" onClick={() => setImage(null)} className="text-muted-foreground hover:text-primary" aria-label="remove"><X className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <button data-testid="quote-submit" type="submit" disabled={loading} className="btn-forge group w-full flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm hover:bg-white disabled:opacity-60">
              {loading ? "Sending…" : <>Send request <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
